import type { ComponentProps, FC } from 'react';
import { memo, useCallback, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import type { ApolloError } from '@frameio/graphql-client';
import type { Workspace } from '@frameio/graphql-client/generated';
import { useGetProjectPermissionsLazyQuery } from '@frameio/graphql-client/generated';
import { useProjectNotificationsStatusFragment } from '@frameio/notifications/gql';
import { useToggleProjectNotifications } from '@frameio/notifications/hooks';
import { ListLinkWithPrefetch } from '@frameio/shared/routing/components/ListLinkWithPrefetch';
import { getLink } from '@frameio/shared/routing/getLink';
import {
  Box,
  List,
  ListItem,
  ListItemContent,
  ListItemContextMenu,
  ListItemLabel,
  ListSection,
  ProjectAvatar,
  RouterProvider,
  Spinner,
  useHoverIntent,
  useOnStateChange,
  useSheetContext,
} from '@frameio/vapor';
import { useIsLimitedToOneWorkspace } from '@frameio/workspaces/shared/hooks/useIsLimitedToOneWorkspace';
import TEXT from './generated/TEXT/ProjectSwitcherProjectList';
import { ProjectSwitcherProjectMenu } from './ProjectSwitcherProjectMenu';
import { useProjectSettings } from './ProjectSwitcherProjectSettings';

type ProjectSwitcherProjectListProps = {
  activeProjectId: string;
  fetchError: ApolloError | undefined;
  groupedProjects: Workspace[];
  initialLoading: boolean;
  loadingMore: boolean;
  query: string;
  listRef: React.RefObject<HTMLUListElement | null>;
  fetchNextPage: () => void;
};

/**
 * id substring to mark a recent item
 */
export const RECENT_WORKSPACE_ID = 'RECENT';
/**
 * id substring to mark a recent item as loading
 * used to render a skeleton for the recent item until loading is done
 */
export const RECENT_LOADING_ID = 'RECENT_LOADING';

TEXT.DEFINE({
  /** Label for All Projects section in the Project Switcher */
  allProjects: 'All Projects',
  /** Aria label for the projects list in the Project Switcher */
  ariaLabelProjects: 'Projects',
  /** Aria label for the projects loading state in the Project Switcher */
  ariaLabelLoading: 'Loading',
  /** Aria label for the project menu toggle in the Project Switcher */
  showProjectMenu: 'Show project menu',
});

export const ProjectSwitcherProjectList: FC<
  ProjectSwitcherProjectListProps
> = ({
  activeProjectId,
  fetchError,
  groupedProjects,
  initialLoading,
  loadingMore,
  query,
  listRef,
  fetchNextPage,
}) => {
  const router = useRouter();
  const { onRequestClose } = useSheetContext();
  const [isPending, startTransition] = useTransition();
  const [activatedHref, setActivatedHref] = useState<string | null>(null);
  const isLimitedToOneWorkspace = useIsLimitedToOneWorkspace();

  useOnStateChange(isPending, () => {
    if (!isPending) {
      onRequestClose();
      setActivatedHref(null);
    }
  });

  let loadingState: ComponentProps<typeof List>['loading'];
  if (initialLoading) {
    loadingState = 'loading';
  } else if (loadingMore) {
    loadingState = 'loading-more';
  } else if (fetchError) {
    if (groupedProjects.length === 0) {
      loadingState = 'loading-error';
    } else {
      loadingState = 'loading-more-error';
    }
  }

  return (
    <RouterProvider
      LinkComponent={ListLinkWithPrefetch as unknown as React.ComponentType}
      navigate={(href: string) => {
        setActivatedHref(href);
        startTransition(() => {
          router.push(href);
        });
      }}
    >
      <List
        ref={listRef}
        animate={false}
        aria-label={TEXT.ariaLabelProjects}
        defaultSelectedKeys={[activeProjectId]}
        disallowEmptySelection={true}
        id="search-list"
        linkBehavior="selection"
        loading={loadingState}
        onBoundary={(boundary) => {
          if (boundary === 'bottom') {
            fetchNextPage();
          }
        }}
        sectionGap={16}
        selectedKeys={[activeProjectId]}
        selectionBehavior="toggle"
        selectionMode="single"
        shouldFocusWrap={true}
        variant="nav"
      >
        {groupedProjects.map((workspace) => (
          <ListSection
            key={workspace.id}
            isHeaderVisible={true}
            // If the account is limited to one workspace, we don't want to show the workspace name.
            title={
              workspace.id === RECENT_WORKSPACE_ID || !isLimitedToOneWorkspace
                ? workspace.name
                : TEXT.allProjects
            }
          >
            {workspace?.projects?.nodes.map((project) => {
              const href = getLink({
                page: 'folder',
                projectId: project.id,
              });

              return (
                <ListItem key={`${workspace.id}:${project.id}`} href={href}>
                  {project.id.includes(RECENT_LOADING_ID) ? null : (
                    <ProjectSwitcherNavItem
                      avatarSrc={project.images?.image64 as string}
                      id={project.id}
                      isInactive={Boolean(project.archivedAt)}
                      isPending={activatedHref === href && isPending}
                      isPrivate={project.private}
                      name={project.name}
                      query={query}
                      workspaceId={workspace.id}
                    />
                  )}
                </ListItem>
              );
            }) || []}
          </ListSection>
        ))}
      </List>
    </RouterProvider>
  );
};

type ProjectSwitcherNavItemProps = {
  name: string;
  id: string;
  query?: string;
  workspaceId: string;
  isPending: boolean;
  avatarSrc: string;
  isInactive: boolean;
  isPrivate: boolean;
};

const ProjectSwitcherNavItem = memo(
  ({
    name,
    id,
    query,
    workspaceId,
    isPending,
    avatarSrc,
    isInactive,
    isPrivate,
  }: ProjectSwitcherNavItemProps) => {
    const { clickProjectSettings } = useProjectSettings();

    const project = useProjectNotificationsStatusFragment({
      from: {
        __typename: 'Project',
        id,
      },
    });

    const { toggleProjectNotificationsMutation: toggleProjectNotifications } =
      useToggleProjectNotifications({
        projectId: id,
      });

    const [loadProjectPermissions, { data: projectPermissionsData }] =
      useGetProjectPermissionsLazyQuery();

    const { bind: hoverIntent } = useHoverIntent(() => {
      if (id) {
        loadProjectPermissions({
          variables: { projectId: id },
        });
      }
    });

    const hasNotificationsEnabled =
      project?.data?.notificationsEnabled ?? false;

    const canUpdate =
      projectPermissionsData?.account?.project?.permissions?.canUpdate;

    const onClickSettings = useCallback(() => {
      if (id && workspaceId) {
        clickProjectSettings(id, workspaceId);
      }
    }, [clickProjectSettings, id, workspaceId]);

    const onMenuRender = useCallback(() => {
      // In case hover intent did not fire
      // https://frame-io.slack.com/archives/C018SJHEERL/p1734043822542669?thread_ts=1734029305.031789&cid=C018SJHEERL
      if (!projectPermissionsData) {
        loadProjectPermissions({
          variables: { projectId: id },
        });
      }
    }, [loadProjectPermissions, id, projectPermissionsData]);

    let adornmentAfter = null;

    if (isPending) {
      adornmentAfter = (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          width="24px"
        >
          <Spinner
            aria-label={TEXT.ariaLabelLoading}
            delay={0}
            isLoading={true}
            size="x-small"
          />
        </Box>
      );
    } else {
      adornmentAfter = (
        <ListItemContextMenu
          buttonAccessibilityLabel={TEXT.showProjectMenu}
          content={
            <ProjectSwitcherProjectMenu
              hasNotificationsEnabled={hasNotificationsEnabled}
              onMount={onMenuRender}
              onSelectNotifications={toggleProjectNotifications}
              {...(canUpdate ? { onSettingsClick: onClickSettings } : {})}
            />
          }
          placement="bottom-start"
          variant="default"
        />
      );
    }

    return (
      <Box width="100%" {...hoverIntent}>
        <ListItemContent
          key={id}
          adornmentAfter={adornmentAfter}
          adornmentBefore={
            <ProjectAvatar
              id={id}
              isInactive={isInactive}
              isPrivate={isPrivate}
              size={24}
              src={avatarSrc}
              title={name}
            />
          }
          data-testid={`project-nav--${name}`}
          label={<ListItemLabel highlight={query}>{name}</ListItemLabel>}
        />
      </Box>
    );
  }
);
